import { createAsyncThunk, createSlice, } from '@reduxjs/toolkit'
import { Holiday, HolidayBookingProcessingState } from '@features/holidays/models/Holiday.model';
import { AppState } from '@app/store/state/app.state';
import {
  getHolidayBooking,
  postNewHoliday,
  deleteHolidayBookingRequest,
  updateNewHoliday
} from '@features/holidays/services/HolidayService';
import { redirectTo } from '@shared/store/auth.duck';
import { failureNotification, successNotification } from '@shared/store/notification.duck';

export interface HolidayBookingState {
  bookingProcessingState: HolidayBookingProcessingState;
  editingHoliday?: any;
  bookedHoliday?: any;
}

export const initialHolidayBooking: HolidayBookingState = {
  bookingProcessingState: HolidayBookingProcessingState.none
}

const saveHoliday: any = createAsyncThunk(
  'holidays/saveHoliday',
  async (holidayBooking: Holiday, thunkAPI) => {
    try {
      if (holidayBooking.id) {
        return await updateNewHoliday(holidayBooking.id, holidayBooking.name, holidayBooking.emailCaledarEvent, holidayBooking.fromDate(), holidayBooking.toDate());
      } else {
        return await postNewHoliday(holidayBooking.name, holidayBooking.emailCaledarEvent, holidayBooking.fromDate(), holidayBooking.toDate());
      }
    } catch (err: any) {
      thunkAPI.dispatch(failureNotification(err.message));
      throw err;
    }
  }
);

const loadEditingHoliday: any = createAsyncThunk(
  'holidays/loadEditingHoliday',
  async (holidayBooking: number, thunkAPI) => {
    await new Promise((resolve: any) => setTimeout(() => resolve(), 1500));
    return await getHolidayBooking(holidayBooking);
  }
);

const deleteHolidayBooking: any = createAsyncThunk(
  'holidays/deleteBooking',
  async (force: any, thunkAPI) => {
    const appState = thunkAPI.getState() as AppState;
    if (appState.holidayBooking.editingHoliday) {
      await deleteHolidayBookingRequest(appState.holidayBooking.editingHoliday.bookingEntity.id);
      await thunkAPI.dispatch(successNotification('Holiday booking deleted'));
      thunkAPI.dispatch(redirectTo('/holidays'));
    }
  }
);

const holidayBookingSlice = createSlice({
  name: 'holidayBooking',
  initialState: initialHolidayBooking,
  reducers: {
    reset: () => initialHolidayBooking,
    clearHolidayBooking: () => initialHolidayBooking
  },
  extraReducers: (builder) => {
    builder
      .addCase(deleteHolidayBooking.pending, (state) => ({...state, bookingProcessingState: HolidayBookingProcessingState.pending, }))
      .addCase(deleteHolidayBooking.rejected, (state) => ({...state, bookingProcessingState: HolidayBookingProcessingState.none, }))
      .addCase(deleteHolidayBooking.fulfilled, (state, action) => ({
      ...state,
      bookingProcessingState: HolidayBookingProcessingState.done,
      editingHoliday: null,
      bookedHoliday: null,
    }))
      .addCase(saveHoliday.pending, (state) => ({...state, bookingProcessingState: HolidayBookingProcessingState.pending, }))
      .addCase(saveHoliday.rejected, (state) => ({...state, bookingProcessingState: HolidayBookingProcessingState.none, }))
      .addCase(saveHoliday.fulfilled, (state, action) => {
      return {
        ...state,
        bookingProcessingState: HolidayBookingProcessingState.done,
        bookedHoliday: action.payload
      }
    })
      .addCase(loadEditingHoliday.pending, (state) => ({...state, bookingProcessingState: HolidayBookingProcessingState.pending, }))
      .addCase(loadEditingHoliday.rejected, (state) => ({...state, bookingProcessingState: HolidayBookingProcessingState.none, }))
      .addCase(loadEditingHoliday.fulfilled, (state, action) => ({
      ...state,
      bookingProcessingState: HolidayBookingProcessingState.none,
      editingHoliday: action.payload
    }));
  }
});


export default holidayBookingSlice.reducer;

// Selectors
const selectBookingState = (state: AppState) => state.holidayBooking.bookingProcessingState;
const selectEditingHoliday = (state: AppState) => state.holidayBooking.editingHoliday;
const selectBookedHoliday = (state: AppState) => state.holidayBooking.bookedHoliday;
